// ============================================================
// BarChart - Reusable SVG bar chart with responsive viewBox.
// Zero external dependencies. Renders horizontal or vertical bars.
// ============================================================

interface BarData {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  data: BarData[];
  /** Chart width in viewBox units (default 300). */
  width?: number;
  /** Chart height in viewBox units (default 150). */
  height?: number;
  /** Default bar color (default '#c4a35a'). */
  barColor?: string;
  /** Show value labels above bars (default true). */
  showValues?: boolean;
  /** Format value for display (default: fixed 0 for >=1, fixed 1 otherwise). */
  formatValue?: (v: number) => string;
  /** Optional annotation line at a specific value (e.g., expected value). */
  annotationValue?: number;
  /** Label for the annotation line. */
  annotationLabel?: string;
  /** Max value override (defaults to max in data). */
  maxValue?: number;
}

const PADDING = { top: 20, right: 10, bottom: 24, left: 10 };

function defaultFormat(v: number): string {
  if (v === 0) return '0';
  return v >= 1 ? v.toFixed(0) : v.toFixed(1);
}

export default function BarChart({
  data,
  width = 300,
  height = 150,
  barColor = '#c4a35a',
  showValues = true,
  formatValue = defaultFormat,
  annotationValue,
  annotationLabel,
  maxValue: maxOverride,
}: BarChartProps) {
  if (data.length === 0) return null;
  const plotW = width - PADDING.left - PADDING.right;
  const plotH = height - PADDING.top - PADDING.bottom;
  const maxVal = maxOverride ?? Math.max(...data.map((d) => d.value), 0.01);
  const barW = Math.max(1, (plotW - BAR_GAP * (data.length - 1)) / data.length);
  const baseY = PADDING.top + plotH;
  /** A value's height on the plot (the same sum the old inline maths did, to the last float digit). */
  const heightOf = (v: number) => (maxVal > 0 ? (v / maxVal) * plotH : 0);
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%' }}
    >
      {data.map((d, i) => (
        <Bar
          key={i}
          d={d}
          x={PADDING.left + i * (barW + BAR_GAP)}
          height={heightOf(d.value)}
          width={barW}
          baseY={baseY}
          fill={d.color || barColor}
          valueText={showValues && d.value > 0 ? formatValue(d.value) : null}
        />
      ))}
      <line x1={PADDING.left} y1={baseY} x2={PADDING.left + plotW} y2={baseY} stroke="#555" strokeWidth={0.5} />
      {annotationValue != null && maxVal > 0 && (
        <Annotation
          y={PADDING.top + plotH - heightOf(annotationValue)}
          x1={PADDING.left}
          x2={PADDING.left + plotW}
          label={annotationLabel}
        />
      )}
    </svg>
  );
}

const BAR_GAP = 2;

interface BarProps {
  d: BarData;
  x: number;
  height: number;
  width: number;
  /** The baseline's y: the bar stands on it and its label sits below. */
  baseY: number;
  fill: string;
  /** The value label above the bar, or null for none. */
  valueText: string | null;
}

function Bar({ d, x, height, width, baseY, fill, valueText }: BarProps) {
  const y = baseY - height;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} rx={1} opacity={0.85} />
      {valueText !== null && (
        <text x={x + width / 2} y={y - 3} textAnchor="middle" fontSize={7} fill="#aaa">
          {valueText}
        </text>
      )}
      <text x={x + width / 2} y={baseY + 12} textAnchor="middle" fontSize={7} fill="#777">
        {d.label}
      </text>
    </g>
  );
}

/** A dashed line at a value (an expected value, a median) and its label. */
function Annotation({ y, x1, x2, label }: { y: number; x1: number; x2: number; label?: string }) {
  return (
    <g>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="#f59e0b" strokeWidth={0.8} strokeDasharray="3,2" />
      {label && (
        <text x={x2 - 2} y={y - 3} textAnchor="end" fontSize={7} fill="#f59e0b">
          {label}
        </text>
      )}
    </g>
  );
}
